import React, { useState } from "react";
import PropTypes from "prop-types";
import { IconButton, InlineWaiting } from "components/ui";
import SaveIcon from "react-icons/lib/md/save";
import CancelIcon from "react-icons/lib/md/cancel";
import SearchableDropdown from "components/searchable-dropdown";
import MdDelete from "react-icons/lib/md/delete";
import { formatAddressNoNull, formatDate, formatPhone } from "utils/misc";
import { Ul, AttributeItem } from "components/lists";
import { STATES } from "utils/enum";

const EditPatientInsurance = ({
  patientInsurance,
  insuranceOptions,
  guarantorOptions,
  toggleInsurance,
  updatePatientInsurance,
  insuranceOrder
}) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [insuranceEdit, setInsuranceEdit] = useState({
    insurance_company_id: patientInsurance.insurance_company_id,
    group_number: patientInsurance.group_number,
    insurance_phone: patientInsurance.insurance_phone,
    policy_number: patientInsurance.policy_number,
    guarantor_name: patientInsurance.guarantor_name,
    guarantor_relation: patientInsurance.guarantor_relation,
    guarantor_dob: formatDate(
      patientInsurance.guarantor_dob,
      "YYYY-MM-DD"
    ),
    policy_start_date: formatDate(
      patientInsurance.policy_start_date,
      "YYYY-MM-DD"
    ),
    policy_end_date: formatDate(patientInsurance.policy_end_date, "YYYY-MM-DD"),
    primary_order: insuranceOrder == "Primary" ? 1 : 2,
    insurance_type_id: patientInsurance.insurance_type_id, 
    insurance_address: {
      street_address: patientInsurance.insurance_address.street_address,
      street_address2: patientInsurance.insurance_address.street_address2,
      city: patientInsurance.insurance_address.city,
      state: patientInsurance.insurance_address.state,
      zip: patientInsurance.insurance_address.zip,
    },
  
  });

  const inputStyle = {
    width: "auto",
    height: "auto",
    padding: "0 15px",
    lineHeight: "1.6",
    marginBottom: 3
  };

  const handleChanges = (e) => {
    const { name, value } = e.target;
  
    if (name.startsWith("insurance_address.")) {
      const addressField = name.split(".")[1];
      setInsuranceEdit((prev) => ({
        ...prev,
        insurance_address: {
          ...prev.insurance_address,
          [addressField]: value,
        },
      }));
    } else {
      setInsuranceEdit((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const submitPatientChanges = async () => {
    setIsUpdating(true);
    await updatePatientInsurance(insuranceEdit);
    setIsUpdating(false);
    toggleInsurance();
  };

  const submitDeleteInsurance = async () => {
    if (confirm("Are you sure you want to delete this?"))
      try {
        setIsUpdating(true);
        await updatePatientInsurance({
          ...insuranceEdit,
          insurance_company_id: "",
          insurance_type_id: ""
        });
        setIsUpdating(false);
      } catch (e) {
        setIsUpdating(false);
      }
    else toggleInsurance();
  };
  const clearInsuranceChanges = () => {
    setInsuranceEdit(null);
  };

  return (
    <div className="patient-insurance-edit">
      <div className="insurance-section-header">
        {insuranceOrder}{" "}
        {isUpdating ? (
          <span style={{ float: "right", maxWidth: 25, paddingRight: 5 }}>
            <InlineWaiting />
          </span>
        ) : (
          <span style={{ float: "right" }}>
            {/* only display delete button if we are looking at secondary insurance and it exists */}
            {patientInsurance?.insurance_company_id != null &&
              insuranceOrder == "Secondary" && (
                <IconButton
                  onClick={() => {
                    const confirmation = confirm(
                      "Are you sure you want to delete insurance?"
                    );
                    if (confirmation) {
                      submitDeleteInsurance();
                      toggleInsurance();
                    }
                  }}
                  text="Delete"
                  style={{ color: "red" }}
                  icon={<MdDelete />}
                  type="button"
                />
              )}
            <IconButton
              onClick={() => {
                toggleInsurance(), clearInsuranceChanges();
              }}
              text="Cancel"
              style={{ color: "gray" }}
              icon={<CancelIcon />}
              type="button"
            />
            <IconButton
              text="Save"
              style={{ color: "green" }}
              icon={<SaveIcon />}
              onClick={submitPatientChanges}
            />
          </span>
        )}
      </div>
      <Ul>
        <AttributeItem title="Insurance:">
          <SearchableDropdown
            options={[{ text: "", value: "", key: "" }, ...insuranceOptions]}
            selected={insuranceEdit.insurance_company_id}
            onSelect={value => {
              setInsuranceEdit(ins => ({
                ...ins,
                insurance_company_id: value
              }));
            }}
          />
        </AttributeItem>
        <AttributeItem title="Insurance Type:">
          {patientInsurance.insurance_type_name}
        </AttributeItem>
        {insuranceEdit.insurance_company_id && (
          <>
          <AttributeItem title="Insurance Phone:">
                <input
                  name="insurance_phone"
                  type="tel"
                  value={formatPhone(insuranceEdit.insurance_phone)}
                  onChange={(e) => {
                    const rawValue = e.target.value;
                    const normalizedValue = rawValue.replace(/\D/g, ""); // Remove non-numeric characters
                    if (normalizedValue.length <= 15) { // Limit to 15 digits (in case of weird non US numbers)
                      setInsuranceEdit((prev) => ({
                        ...prev,
                        insurance_phone: normalizedValue,
                      }));
                    }
                  }}
                  style={{ height: "1rem", margin: 0 }}
                />
              </AttributeItem>
            <AttributeItem title="Policy Number:">
              <input
                style={{ height: "1rem", margin: 0 }}
                type="text"
                onChange={e => handleChanges(e)}
                name="policy_number"
                value={insuranceEdit.policy_number}
              />
            </AttributeItem>
            <AttributeItem title="Group Number:">
              <input
                style={{ height: "1rem", margin: 0 }}
                type="text"
                onChange={e => handleChanges(e)}
                name="group_number"
                value={insuranceEdit.group_number}
              />
            </AttributeItem>
            <AttributeItem title="Guarantor Name:">
            <input
                style={{ height: "1rem", margin: 0 }}
                type="text"
                onChange={e => handleChanges(e)}
                name="guarantor_name"
                value={insuranceEdit.guarantor_name}
              />
            </AttributeItem>
            <AttributeItem title="Guarantor Relation:">
              <select
                style={{
                  height: "1.1rem",
                  margin: 0,
                  padding: "0px 0px 0px 5px",
                  display: "flex",
                  alignItems: "center"
                }}
                onChange={handleChanges}
                name="guarantor_relation"
                value={insuranceEdit.guarantor_relation}
              >
                <option />
                {guarantorOptions.map(({ text, value, key }) => (
                  <option key={key} value={value}>
                    {text}
                  </option>
                ))}
              </select>
            </AttributeItem>

            <AttributeItem title="Guarantor DOB:">
            <input
                style={{ height: "1rem", margin: 0 }}
                type="date"
                onChange={e => handleChanges(e)}
                name="guarantor_dob"
                value={insuranceEdit.guarantor_dob}
              />
            </AttributeItem>
            <AttributeItem title="Start Date:">
              <input
                style={{ height: "1rem", margin: 0 }}
                type="date"
                onChange={e => handleChanges(e)}
                name="policy_start_date"
                value={insuranceEdit.policy_start_date}
              />
            </AttributeItem>
            <AttributeItem title="End Date:">
              <input
                style={{ height: "1rem", margin: 0 }}
                type="date"
                onChange={e => handleChanges(e)}
                name="policy_end_date"
                value={insuranceEdit.policy_end_date}
              />
            </AttributeItem>

            <AttributeItem title="Insurance Address:">
              <div
    style={{
      display: "block",
      whiteSpace: "wrap",
      overflow: "hidden",
    }}
              >{formatAddressNoNull(insuranceEdit.insurance_address)}</div>
            </AttributeItem>
            <div
    style={{
      display: "flex",
      justifyContent: "space-between",
      flexWrap: "wrap",
    }}
  >
  <div style={{ flex:1}}>
    <label htmlFor="street_address">Street Address Line 1</label>
    <input
      id="street_address"
      name="insurance_address.street_address"
      type="text"
      maxLength={75}
      style={{ ...inputStyle }}
      value={insuranceEdit.insurance_address.street_address}
      onChange={e => handleChanges(e)}
    />
  </div>
  <div style={{ flex:1 }}>
    <label htmlFor="street_address2">Street Address Line 2</label>
    <input
      id="street_address2"
      name="insurance_address.street_address2"
      type="text"
      maxLength={75}
      style={{ ...inputStyle }}
      value={insuranceEdit.insurance_address.street_address2}
      onChange={e => handleChanges(e)}
    />
  </div>
    <div style={{ flex:1 }}>
      <label htmlFor="city">City</label>
      <input
        id="city"
        name="insurance_address.city"
        type="text"
        style={inputStyle}
        value={insuranceEdit.insurance_address.city}
        maxLength={75}
        onChange={e => handleChanges(e)}
      />
    </div>
    <div style={{ flex:1 }}>
      <label htmlFor="state">State</label>
      <select
        id="state"
        name="insurance_address.state"
        style={inputStyle}
        value={insuranceEdit.insurance_address.state}
        onChange={e => handleChanges(e)}
      >
        <option value="">Select State</option>
        {STATES.map(({ value, text }) => (
    <option key={value} value={value}>
      {text}
    </option>
  ))}
      </select>
    </div>
    <div style={{ flex:1 }}>
      <label htmlFor="zip">Zip</label>
      <input
        id="zip"
        name="insurance_address.zip"
        type="text"
        style={inputStyle}
        value={insuranceEdit.insurance_address.zip}
        onChange={e => handleChanges(e)}
        maxLength={10}
      />
  </div>
</div>
          </>
        )}
      </Ul>
    </div>
  );
};

EditPatientInsurance.propTypes = {
  patientId: PropTypes.string.isRequired,
  patientInsurance: PropTypes.object.isRequired,
  insuranceOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  updatePatientInsurance: PropTypes.func.isRequired,
  guarantorOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  readOnly: PropTypes.bool.isRequired,
  toggleInsurance: PropTypes.func.isRequired,
  insuranceOrder: PropTypes.oneOf(["Primary", "Secondary"])
};

EditPatientInsurance.defaultProps = {
  readOnly: false
};

export default EditPatientInsurance;
